// =============================================================================
// MeuDriver — Help / FAQ Page
// =============================================================================

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, HelpCircle, Car, Users, CreditCard, MessageCircle } from 'lucide-react'
import { useSEO } from '@/hooks/useSEO'
import { cn } from '@/lib/utils'

interface FAQItem {
  q: string
  a: string
}

interface FAQSection {
  title: string
  icon: React.ReactNode
  items: FAQItem[]
}

const FAQ_SECTIONS: FAQSection[] = [
  {
    title: 'Para alunos',
    icon: <Users className="h-4 w-4" />,
    items: [
      {
        q: 'Como agendar minha primeira aula?',
        a: 'Após criar sua conta e fazer o onboarding, vá até "Buscar instrutores", escolha o perfil que mais combina com você, clique em "Agendar aula" e siga os três passos: escolha a data e horário, informe o endereço de partida e confirme. A aula fica com status "Aguardando pagamento" até que o pagamento seja processado.',
      },
      {
        q: 'Posso cancelar ou reagendar uma aula?',
        a: 'Sim. Você pode cancelar ou reagendar até 2 horas antes do horário agendado. Cancelamentos com mais de 24h de antecedência recebem reembolso integral. Entre 2h e 24h, é cobrada uma taxa de 30%. Após o início da aula não há reembolso.',
      },
      {
        q: 'O que são pacotes de aulas?',
        a: 'Pacotes permitem pré-comprar 3, 5 ou 10 aulas com desconto de 10%, 15% ou 20%, respectivamente. Ao escolher um instrutor, selecione "Pacote" no tipo de aula e escolha a quantidade. O valor total é cobrado uma única vez.',
      },
      {
        q: 'Como funciona a aula experimental?',
        a: 'A aula experimental tem duração fixa de 30 minutos com 50% de desconto. É ideal para conhecer o instrutor antes de se comprometer com aulas completas.',
      },
      {
        q: 'Posso favoritar instrutores?',
        a: 'Sim. Clique no ícone de coração no card ou perfil do instrutor para adicioná-lo aos seus favoritos. Eles ficam salvos no seu dashboard para acesso rápido.',
      },
      {
        q: 'Como faço para avaliar o instrutor?',
        a: 'Após a conclusão da aula, acesse os detalhes da aula e role até a seção "Avaliar instrutor". Você pode dar uma nota de 1 a 5 estrelas, adicionar elogios rápidos e escrever um comentário.',
      },
    ],
  },
  {
    title: 'Para instrutores',
    icon: <Car className="h-4 w-4" />,
    items: [
      {
        q: 'Como me cadastro como instrutor?',
        a: 'Clique em "Seja instrutor" no menu principal e preencha o formulário de cadastro. Após a criação da conta, você passará pelo onboarding de 5 etapas: dados pessoais, informações da CNH, fotos e dados do veículo, preço e raio de atendimento, e disponibilidade semanal. Seu cadastro ficará em análise por até 48h.',
      },
      {
        q: 'Quanto tempo leva a aprovação?',
        a: 'Nossa equipe analisa os cadastros em até 48 horas úteis. Você receberá uma notificação quando sua conta for aprovada.',
      },
      {
        q: 'Como defino meu preço?',
        a: 'Na edição de perfil, aba "Dados pessoais", defina sua taxa por hora (R$/h). Você também pode configurar preços diferenciados por especialidade na aba "Especialidades". A plataforma cobra uma comissão de 15% sobre cada aula concluída.',
      },
      {
        q: 'Como gerencio minha agenda?',
        a: 'Acesse "Agenda" no menu lateral. Você pode definir sua disponibilidade semanal recorrente, bloquear datas específicas e visualizar todas as aulas confirmadas em um calendário semanal.',
      },
      {
        q: 'Quando recebo o pagamento?',
        a: 'O repasse é liberado após a conclusão de cada aula. O valor aparece em "Financeiro" como pendente até que seja processado (normalmente em até 3 dias úteis).',
      },
      {
        q: 'Como vejo o histórico de alunos?',
        a: 'Acesse "Meus alunos" no menu lateral. Lá você encontra todos os alunos que já agendam aulas com você, com estatísticas como total de aulas, valor gasto e data da última aula.',
      },
    ],
  },
  {
    title: 'Pagamentos',
    icon: <CreditCard className="h-4 w-4" />,
    items: [
      {
        q: 'Quais formas de pagamento são aceitas?',
        a: 'Aceitamos cartão de crédito (parcelado em até 12x), PIX e boleto bancário através das plataformas Stripe e Mercado Pago.',
      },
      {
        q: 'Como funciona o reembolso?',
        a: 'Reembolsos são processados automaticamente quando aplicável. O valor retorna à forma de pagamento original em até 5 dias úteis para PIX e boleto, e até 2 faturas para cartão de crédito.',
      },
      {
        q: 'A taxa da plataforma está inclusa no preço mostrado?',
        a: 'Não. O preço exibido no perfil do instrutor é o valor da hora de aula. A taxa de serviço da plataforma (15%) é adicionada no resumo final antes da confirmação do pagamento.',
      },
      {
        q: 'Como solicitar nota fiscal?',
        a: 'No detalhe de cada aula concluída, clique em "Imprimir recibo" para gerar um comprovante. Para nota fiscal formal, entre em contato com nosso suporte.',
      },
    ],
  },
  {
    title: 'Suporte',
    icon: <MessageCircle className="h-4 w-4" />,
    items: [
      {
        q: 'Como entro em contato com o suporte?',
        a: 'Você pode usar o chat interno de cada aula para se comunicar com o instrutor. Para questões com a plataforma, envie um e-mail para suporte@meudriver.com.br. Respondemos em até 24h úteis.',
      },
      {
        q: 'O que fazer se o instrutor não compareceu?',
        a: 'Acesse os detalhes da aula, clique em "Abrir disputa" e selecione "Instrutor não compareceu". Nossa equipe analisará o caso em até 48h e providenciará o reembolso se confirmado.',
      },
      {
        q: 'Como excluir minha conta?',
        a: 'Acesse "Meu perfil" e role até a seção "Zona de perigo". Clique em "Excluir conta" e confirme. Todos os seus dados serão removidos de forma permanente. Aulas pendentes devem ser canceladas antes da exclusão.',
      },
    ],
  },
]

function AccordionItem({ item, isOpen, onToggle }: {
  item: FAQItem
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-3 py-4 text-left"
      >
        <span className={cn(
          'text-sm font-medium leading-snug transition-colors',
          isOpen ? 'text-brand-700' : 'text-gray-800',
        )}>
          {item.q}
        </span>
        <ChevronDown className={cn(
          'h-4 w-4 shrink-0 mt-0.5 text-muted-foreground transition-transform duration-200',
          isOpen && 'rotate-180',
        )} />
      </button>
      {isOpen && (
        <div className="pb-4 text-sm text-muted-foreground leading-relaxed">
          {item.a}
        </div>
      )}
    </div>
  )
}

export default function HelpPage() {
  useSEO({
    title: 'Central de Ajuda — MeuDriver',
    description: 'Tire suas dúvidas sobre como usar o MeuDriver: agendamentos, pagamentos, instrutor e muito mais.',
  })

  const [openKey, setOpenKey] = useState<string | null>(null)

  function toggle(key: string) {
    setOpenKey((prev) => (prev === key ? null : key))
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-br from-brand-600 to-teal-600 text-white py-16 px-4 text-center">
        <div className="max-w-2xl mx-auto">
          <HelpCircle className="h-10 w-10 mx-auto mb-4 opacity-80" />
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Central de Ajuda</h1>
          <p className="mt-3 text-brand-100 text-lg">
            Respostas para as principais dúvidas sobre o MeuDriver.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-12 space-y-8">
        {FAQ_SECTIONS.map((section) => (
          <div key={section.title} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-gray-100 bg-gray-50/50">
              <span className="text-brand-600">{section.icon}</span>
              <h2 className="text-base font-bold text-gray-900">{section.title}</h2>
            </div>
            <div className="px-5">
              {section.items.map((item, idx) => {
                const key = `${section.title}-${idx}`
                return (
                  <AccordionItem
                    key={key}
                    item={item}
                    isOpen={openKey === key}
                    onToggle={() => toggle(key)}
                  />
                )
              })}
            </div>
          </div>
        ))}

        {/* CTA */}
        <div className="text-center bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
          <p className="text-gray-700 font-medium mb-2">Não encontrou o que procurava?</p>
          <p className="text-sm text-muted-foreground mb-4">
            Entre em contato pelo e-mail <strong>suporte@meudriver.com.br</strong>
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-600 hover:text-brand-700 transition-colors"
          >
            ← Voltar ao início
          </Link>
        </div>
      </div>
    </div>
  )
}
